import { AccountInfo, InteractionRequiredAuthError } from "@azure/msal-browser";
import { AppRoles, loginRequest } from "./AuthorizationConfig";
import MsalInstance, { msalInit } from "./AuthorizationProvider";
import { pickPreferredAccount } from "./msalGuards";

function popupAlreadyFailedKey(account: AccountInfo): string {
  return `dentifier.msal.popupFailed.${account.homeAccountId}`;
}

export function decodeJwtPayload(token: string): Record<string, unknown> | null {
  try {
    const parts = token.split(".");
    if (parts.length < 2) return null;
    let base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    while (base64.length % 4) base64 += "=";
    const json = atob(base64);
    return JSON.parse(json) as Record<string, unknown>;
  } catch {
    return null;
  }
}

function isInteractionRequiredError(error: unknown): boolean {
  if (error instanceof InteractionRequiredAuthError) {
    return true;
  }
  if (typeof error === "object" && error !== null && "errorCode" in error) {
    const code = (error as { errorCode?: string }).errorCode;
    return (
      code === "interaction_required" ||
      code === "consent_required" ||
      code === "login_required"
    );
  }
  return false;
}

export const getActiveAccount = async () => {
  await msalInit;
  const account = MsalInstance.getActiveAccount();
  if (account) return account;

  const accounts = MsalInstance.getAllAccounts();
  if (accounts.length > 0) {
    const chosen = pickPreferredAccount(accounts);
    MsalInstance.setActiveAccount(chosen);
    return chosen;
  }
  return null;
};


export const getToken = async (): Promise<string | null> => {
  try {
    const account = await getActiveAccount();
    if (!account) return null;

    try {
      const response = await MsalInstance.acquireTokenSilent({
        ...loginRequest,
        account,
      });
      try {
        sessionStorage.removeItem(popupAlreadyFailedKey(account));
      } catch {
        /* ignore */
      }
      return response.accessToken;
    } catch (error) {
      if (isInteractionRequiredError(error)) {
        try {
          if (sessionStorage.getItem(popupAlreadyFailedKey(account)) === "1") {
            console.warn(
              "Skipping acquireTokenPopup; already failed for this account this session."
            );
            return null;
          }
          const response = await MsalInstance.acquireTokenPopup({
            ...loginRequest,
            account,
          });
          try {
            sessionStorage.removeItem(popupAlreadyFailedKey(account));
          } catch {
            /* ignore */
          }
          return response.accessToken;
        } catch (popupError) {
          console.error("acquireTokenPopup failed:", popupError);
          try {
            sessionStorage.setItem(popupAlreadyFailedKey(account), "1");
          } catch {
            /* ignore */
          }
          return null;
        }
      }
      console.log("Silent token acquisition failed", error);
      return null;
    }
  } catch (error) {
    console.error("Token acquisition error:", error);
    return null;
  }
};


export const forceRefreshAccessToken = async (): Promise<string | null> => {
  const account = await getActiveAccount();
  if (!account) return null;

  try {
    const response = await MsalInstance.acquireTokenSilent({
      ...loginRequest,
      account,
      forceRefresh: true,
    });

    if (response.account) {
      MsalInstance.setActiveAccount(response.account);
    }

    try {
      sessionStorage.removeItem(popupAlreadyFailedKey(account));
    } catch {
      /* ignore */
    }

    return response.accessToken;
  } catch (error) {
    console.warn("forceRefreshAccessToken failed:", error);
    return null;
  }
};

export const getUserRoles = async (): Promise<string[] | null> => {
  try {
    const account = await getActiveAccount();
    if (!account) return null;

    const idRoles = account.idTokenClaims?.roles;
    if (Array.isArray(idRoles) && idRoles.length > 0) {
      return idRoles;
    }

    const at = await getToken();
    if (at) {
      const p = decodeJwtPayload(at);
      const atRoles = p?.roles;
      if (Array.isArray(atRoles) && atRoles.length > 0) {
        return atRoles as string[];
      }
    }

    console.warn("No roles found in ID token or access token claims");
    return null;
  } catch (error) {
    console.error("Error getting user roles:", error);
    return null;
  }
};

function hasDentifierAppRoles(roles: string[] | null): boolean {
  if (!roles?.length) return false;
  return roles.some(
    (r) =>
      r === AppRoles.ADMIN ||
      r === AppRoles.CUSTOMER ||
      (typeof r === "string" && r.startsWith("Dentifier."))
  );
}

/**
 * After Entra group / role changes, keep forcing token refresh until issued tokens
 * include app roles (same outcome as signing in again). Bounded wait for propagation delay.
 */
export async function refreshTokensUntilDentifierRoles(
  maxAttempts = 36,
  delayMs = 1500
): Promise<boolean> {
  for (let attempt = 0; attempt < maxAttempts; attempt++) {
    let roles = await getUserRoles();
    if (hasDentifierAppRoles(roles)) {
      return true;
    }
    await forceRefreshAccessToken();
    roles = await getUserRoles();
    if (hasDentifierAppRoles(roles)) {
      return true;
    }
    await new Promise((r) => setTimeout(r, delayMs));
  }
  return false;
}
